import React, { useState } from 'react';
import { CustomModal } from '../../../../components/uikit/modal/CustomModal.tsx';
import { ButtonColor } from '../../../../components/uikit/buttons/Button.tsx';
import clsx from 'clsx';
import styles from '../AccountSettings.module.scss';
import { Input } from '../../../../components/uikit/inputs/Input.tsx';
import { requiredRule } from '../../../../helpers/form/react-hook-form-helper.ts';
import { validatePassword } from '../../../../helpers/password-helper.ts';
import { useScopedTranslation } from '../../../../application/localization/useScopedTranslation.ts';
import { useAdvancedForm } from '../../../../helpers/form/useAdvancedForm.ts';
import { UserMenuCategory } from 'pages/authorized/rootPage/RootPage.tsx';
import { useAnalytics } from '../../../../helpers/analytics.ts';
import { LoadingType } from '../../../../components/animations/LoadingPage.tsx';
import { UserClient } from 'services/api/api-client.ts';
import { useWatch } from 'react-hook-form';
import { logOut } from '../../../../helpers/auth/auth-interceptor.ts';
import { queryClient } from '../../../../services/api/query-client-helper.ts';
import Logger from 'js-logger';

export type Form = {
  passwordOld: string;
  passwordNew: string;
  passwordConfirm: string;
};

type OwnProps = {
  closeModal: () => void;
  changeLoadingStatus: (status: LoadingType) => void;
  loadingStatus: LoadingType;
};

export const ChangePasswordModal = (props: OwnProps) => {
  const i18n = useScopedTranslation('Page.AccountSettingsPage');
  const { trackEvent } = useAnalytics();
  const { closeModal, changeLoadingStatus, loadingStatus } = props;

  const [formSubmitErrorText, setFormSubmitErrorText] = useState('');

  const logout = () => {
    closeModal();
    logOut()
      .then(() => queryClient.clear())
      .catch((e) => Logger.error(e));
  };

  const form = useAdvancedForm<Form>(
    async () => {
      try {
        setFormSubmitErrorText('');
        trackEvent({
          category: UserMenuCategory,
          action: 'Change password',
          name: 'Password changed',
        });

        changeLoadingStatus('loading');

        await UserClient.changePassword({
          oldPassword: passwordOld.trim(),
          newPassword: passwordNew,
        });

        changeLoadingStatus('completed');
        setTimeout(logout, 1500);
      } catch (e: any) {
        setFormSubmitErrorText(e.detail);
        changeLoadingStatus('none');
      }
    },
    {
      defaultValues: {
        passwordOld: '',
        passwordNew: '',
        passwordConfirm: '',
      },
      mode: 'onChange',
    },
  );

  const [passwordOld, passwordNew, passwordConfirm] = useWatch({
    control: form.control,
    name: ['passwordOld', 'passwordNew', 'passwordConfirm'],
  });

  const handleSubmit = async () => {
    const result = await form.trigger();
    if (!result) return;

    if (passwordNew !== passwordConfirm) {
      form.setError('passwordConfirm', {
        message: i18n.t('password_mismatch'),
      });
      return;
    }

    void form.handleSubmitDefault();
  };

  const isFormLoading = loadingStatus === 'loading';

  if (loadingStatus !== 'none') {
    return null;
  }

  return (
    <CustomModal
      isOpen={true}
      onSubmit={handleSubmit}
      onCancel={closeModal}
      cancelButtonText={i18n.t('change_password_cancel')}
      cancelButtonType={ButtonColor.Secondary}
      submitButtonText={i18n.t('change_password_submit')}
      submitButtonType={ButtonColor.Primary}
      titleText={i18n.t('change_password_title')}
      containerClassName={styles.modalBodyWidth480}
      isDisabled={isFormLoading}
      isLoading={isFormLoading}
      formSubmitErrorText={formSubmitErrorText}
    >
      <div
        onClick={(e) => {
          e.stopPropagation();
        }}
        className={clsx(
          styles.columnContainer,
          styles.rowGap20,
          styles.marginBot30,
        )}
      >
        <Input
          type={'password'}
          {...form.register(
            'passwordOld',
            requiredRule(i18n.t('required_error')),
          )}
          onChange={(e) => {
            form.clearErrors('passwordOld');
            form.setValue('passwordOld', e.target.value);
          }}
          errorText={form.formState.errors.passwordOld?.message}
          topHelperText={i18n.t('change_password_old')}
          fullWidth={true}
          containerClassName={styles.marginTop25}
        />
        <Input
          type={'password'}
          onEnterPressed={handleSubmit}
          {...form.register('passwordNew', {
            ...requiredRule(i18n.t('required_error')),
            validate: (value) => validatePassword(value),
          })}
          errorText={form.formState.errors.passwordNew?.message}
          topHelperText={i18n.t('change_password_new')}
          fullWidth={true}
          containerClassName={styles.marginTop25}
        />
        <Input
          type={'password'}
          onEnterPressed={handleSubmit}
          {...form.register('passwordConfirm', {
            ...requiredRule(i18n.t('required_error')),
            validate: (value) => validatePassword(value),
          })}
          errorText={form.formState.errors.passwordConfirm?.message}
          topHelperText={i18n.t('change_password_confirm')}
          fullWidth={true}
          containerClassName={styles.marginTop25}
        />
      </div>
    </CustomModal>
  );
};
